import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Sparkles, Send, RefreshCw, Loader2, MessageSquare } from 'lucide-react';
import { streamAdvisor } from '../../components/group/advisorStream';
import { AdvisorEmailSettings } from '../../components/group/AdvisorEmailSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MD = ({ text }) => (
  <div className="advisor-md">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
  </div>
);

const ChatPanel = ({ sessionId }) => {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API_URL}/api/advisor/chat/${sessionId}`, { headers }).then((r) => setMsgs(r.data.messages)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [msgs]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput(''); setBusy(true);
    setMsgs((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    try {
      await streamAdvisor('chat', { session_id: sessionId, message: text }, {
        onDelta: (d) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; }),
      });
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Card className="bg-slate-800 border-slate-600 min-w-0 lg:sticky lg:top-4" data-testid="advisor-chat">
      <CardHeader className="pb-2"><CardTitle className="text-white text-base flex items-center gap-2"><MessageSquare className="h-4 w-4 text-yellow-400" /> Ask the advisor</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="h-[55vh] overflow-y-auto space-y-3 pr-1" data-testid="advisor-chat-messages">
          {msgs.length === 0 && <p className="text-slate-400 text-sm">Ask follow-up questions, e.g. "Which debt should we pay first?" or "How do we lift spares margin to 40%?"</p>}
          {msgs.map((m, i) => (
            <div key={i} className={`rounded-lg p-3 text-sm ${m.role === 'user' ? 'bg-yellow-500/15 text-yellow-50 ml-8' : 'bg-slate-900/70 text-slate-100 mr-4'}`} data-testid={`advisor-msg-${i}`}>
              {m.role === 'assistant' ? (m.content ? <MD text={m.content} /> : <Loader2 className="h-4 w-4 animate-spin" />) : m.content}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="flex gap-2">
          <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2} placeholder={sessionId ? 'Type your question…' : 'Generate a report first'} disabled={!sessionId || busy}
            className="flex-1 rounded-md bg-slate-700 border border-slate-600 p-2 text-sm text-white resize-none" data-testid="advisor-chat-input" />
          <Button onClick={send} disabled={!sessionId || busy || !input.trim()} className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 self-end" data-testid="advisor-chat-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function FinancialAdvisor() {
  const [report, setReport] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/advisor/report/latest`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => {
        if (!r.data?.id) return;
        setReport(r.data); setText(r.data.report || '');
        if (r.data.status === 'running') poll(r.data.id); else setSessionId(r.data.session_id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const poll = (id) => {
    setBusy(true);
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/advisor/report/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        setText(data.report || '');
        if (data.status === 'running') { setTimeout(tick, 2500); return; }
        setBusy(false); setReport(data);
        if (data.status === 'error') toast.error(`Report failed: ${data.error || 'unknown error'}`);
        else { setSessionId(data.session_id); toast.success('Report ready'); }
      } catch { setTimeout(tick, 5000); }
    };
    tick();
  };

  const generate = async () => {
    setText(''); setSessionId(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/advisor/report`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      poll(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start report'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="financial-advisor-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Sparkles className="h-6 w-6 text-yellow-500" /> AI Financial Advisor</h1>
            <p className="text-slate-600 text-sm">Reads the JSM Group Overview (6 months), drivers' collective totals, Auto Spares P&L, stock, JSM Trading funding and the Debt Register.</p>
          </div>
          <Button onClick={generate} disabled={busy} className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 font-bold" data-testid="advisor-generate-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analysing… (1–3 min)</> : <><RefreshCw className="h-4 w-4 mr-2" /> {text ? 'Generate new report' : 'Generate report'}</>}
          </Button>
        </div>
        <AdvisorEmailSettings reportId={sessionId} reportReady={!!sessionId && !busy} />
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
          <Card className="bg-slate-800 border-slate-600 min-w-0" data-testid="advisor-report">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base">Advisory report {report?.created_at && <span className="text-xs text-slate-400 font-normal ml-2">{new Date(report.created_at).toLocaleString('en-ZA')}{report.created_by ? ` · ${report.created_by}` : ''}</span>}</CardTitle>
            </CardHeader>
            <CardContent data-testid="advisor-report-body">
              {text ? <MD text={text} /> : <p className="text-slate-400 text-sm py-10 text-center">{busy ? 'Collecting figures and analysing…' : 'No report yet. Press "Generate report". Capture all debts in the Debt Register on the Group Overview first for the best advice.'}</p>}
            </CardContent>
          </Card>
          <ChatPanel sessionId={busy ? null : sessionId} />
        </div>
      </div>
    </DashboardLayout>
  );
}
